import type { User } from './types';

/* acp 2026-07-24 (Ryan) — Artist CNTRL Pro switch.
 *
 * ACP is an account UPGRADE (billed, plan_tier === 'acp'), for artists
 * playing 3K+ rooms. Same app, same domain, roster pattern: exactly like
 * belongsOnRoster() routes org roots to /app/roster, isPro() decides which
 * VARIANT of a section renders. Only three sections differ — Booking,
 * Venue assets, Contacts — everything else is shared and MUST NOT fork.
 *
 * Rules (do not relitigate at 2am):
 *  · STRICT key match. Legacy rows may carry 'artist_pro' from the old
 *    5-tier ladder — that was a price point of standard AC, NOT ACP.
 *    endsWith('_pro') would silently upgrade those rows; never do that.
 *  · Never infer Pro from data (cap sizes, gear, show count). The flag is
 *    flipped by billing (Stripe webhook → users.plan_tier) or manually by
 *    Ryan via wrangler until checkout is wired.
 *  · Gate at the section/route level, one check per section — not sprinkled
 *    per-widget (the VENUE_KEYS lesson: scattered logic drifts).
 */
export function isPro(u: User | null | undefined): boolean {
  if (!u) return false;
  /* jedi-exception 2026-07-29 (Ryan: "the Jedi should be the exception and
     see everything") — platform staff pass every Pro gate regardless of the
     band's tier. View-as still simulates correctly: applyViewAs swaps role
     AND plan before the user reaches this check, so a jedi simulating a
     standard seat sees standard. */
  const role = String((u as { membership_role?: string }).membership_role || (u as { role?: string }).role || '').toLowerCase();
  if (role === 'jedi' || role === 'admin') return true;
  return String(u.plan_tier || '').toLowerCase() === 'acp';
}

/* ── acpro-billing 2026-07-28 (Ryan: "Primary / Bandmember AV / Managers /
   Labels / Booking agents all should have the option to upgrade or downgrade"
   + "wire Stripe checkout now") — self-serve Pro billing helpers, shared by
   the artist SettingsDrawer and the roster SettingsModal. The worker is the
   real gate (role + band resolution via Bearer + X-Band-Id); these are thin
   fetchers. Upgrade redirects to Stripe Checkout; the webhook flips
   plan_tier, so the UI shows "may take a minute" after return. ── */
import { apiGet, apiPost } from './api';

export interface ProPlanStatus {
  readonly plan_tier: string;
  readonly is_pro: boolean;
  readonly has_subscription: boolean;
  readonly cancel_at: string | null;
}

/* Surface the WORKER's error text, not ApiError's generic "Request failed:
   400 …" — the server says actionable things ("Pro price not configured —
   create the ACP price in Stripe…") and Ryan needs to see them. */
function proErrMsg(e: unknown, fallback: string): string {
  const ae = e as { body?: string | null; message?: string } | null;
  if (ae && typeof ae.body === 'string') {
    try {
      const j = JSON.parse(ae.body) as { error?: string };
      if (j && j.error) return String(j.error);
    } catch { /* not JSON */ }
  }
  return (ae && ae.message) || fallback;
}

export async function fetchProStatus(): Promise<ProPlanStatus | null> {
  try {
    const res = await apiGet<{ ok?: boolean } & ProPlanStatus>('auth', '/api/billing/pro/status');
    return res && res.ok ? res : null;
  } catch { return null; }
}

/** Upgrade to Pro. Bands with an active base subscription get a PRICE SWAP
    (acpro-price-swap 2026-07-28: Pro replaces standard — Stripe prorates,
    no redirect) → resolves { swapped: true }. Bands with no base
    subscription get a Checkout Session → resolves { url } to redirect.
    cadence 'annual' (default, $250/mo billed yearly) | 'monthly'. */
export async function startProCheckout(cadence: 'annual' | 'monthly' = 'annual'): Promise<{ url?: string; swapped?: boolean; error?: string }> {
  try {
    const res = await apiPost<{ ok?: boolean; url?: string; swapped?: boolean; error?: string }>('auth', '/api/billing/pro/checkout', { cadence });
    if (res && res.ok && res.swapped) return { swapped: true };
    if (res && res.ok && res.url) return { url: res.url };
    return { error: (res && res.error) || 'Could not start checkout.' };
  } catch (e) {
    return { error: proErrMsg(e, 'Could not start checkout.') };
  }
}

/** Downgrade (cancel at period end; immediate for comped Pro). resume=true
    un-cancels a pending downgrade. */
export async function cancelPro(resume?: boolean): Promise<{ ok: boolean; immediate?: boolean; cancel_at?: string | null; error?: string }> {
  try {
    const res = await apiPost<{ ok?: boolean; immediate?: boolean; cancel_at?: string | null; error?: string }>(
      'auth', '/api/billing/pro/cancel', resume ? { resume: true } : {});
    return { ok: !!(res && res.ok), immediate: res?.immediate, cancel_at: res?.cancel_at ?? null, error: res?.error };
  } catch (e) {
    return { ok: false, error: proErrMsg(e, 'Request failed.') };
  }
}
